/**
 * The lock screen. A2 owns it.
 *
 * A soft lock keeps the session row alive and blanks the screen. Unlocking rotates the
 * session id and drops the operator back exactly where they were. The absolute 12 hour
 * limit still applies underneath: an expired session sends them to /login instead, and the
 * API says which by returning `session_locked` or `unauthenticated`.
 *
 * In PIN mode (features/auth/mode.ts) the PIN pad is the whole screen. The 5 minute packer
 * idle lock is the server's: it answers `session_locked` and the API client lands here.
 */
import { useEffect, useState } from 'react';
import { createRoute, useSearch } from '@tanstack/react-router';
import { Route as rootRoute } from '../__root';
import { PinPad } from '@/features/auth/PinPad';
import { hasDeviceHint } from '@/features/auth/device';
import { useAuthMode } from '@/features/auth/mode';
import { AuthModeUnreachable } from '@/features/auth/AuthModeUnreachable';
import { loginErrorMessage, passkeyUnlock, pinUnlock } from '@/features/auth/api';
import { ApiProblem } from '@/lib/api';
import '@/features/auth/auth.css';

interface LockSearch {
  next?: string;
}

function landing(next: string | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return '/';
  return next;
}

export function LockScreen() {
  const search = useSearch({ strict: false }) as LockSearch;
  const auth = useAuthMode();
  const pinOnly = auth.mode === 'pin';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPin, setShowPin] = useState(false);
  const [resetToken, setResetToken] = useState(0);
  const [deviceKnown, setDeviceKnown] = useState(false);

  useEffect(() => {
    setDeviceKnown(hasDeviceHint());
  }, []);

  async function run(work: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await work();
      window.location.assign(landing(search.next));
    } catch (err) {
      // An absolute expiry while locked is a different screen, not a different message.
      if (err instanceof ApiProblem && err.code === 'unauthenticated') {
        window.location.assign(`/login?next=${encodeURIComponent(landing(search.next))}`);
        return;
      }
      setError(loginErrorMessage(err));
      setResetToken((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="a-auth">
      <div className="a-auth__card">
        <div className="a-auth__mark" aria-hidden="true" />
        <h1 className="a-auth__title">Locked</h1>
        <p className="a-auth__hint">This console locked itself. Your work is still here.</p>

        {error ? (
          <p className="a-error" role="alert">
            {error}
          </p>
        ) : null}

        {auth.unreachable ? <AuthModeUnreachable onRetry={auth.retry} /> : null}
        {!auth.resolved && !auth.unreachable ? <p className="a-auth__hint">One moment.</p> : null}

        {auth.resolved && (pinOnly || showPin) ? (
          <>
            <PinPad
              disabled={busy}
              resetToken={resetToken}
              onSubmit={(pin) => void run(() => pinUnlock(pin))}
              label="Enter your PIN to unlock"
            />
            {!pinOnly ? (
              <button type="button" className="a-btn a-btn--quiet" onClick={() => setShowPin(false)} disabled={busy}>
                Use a passkey instead
              </button>
            ) : null}
          </>
        ) : auth.resolved ? (
          <>
            <button
              type="button"
              className="a-btn a-btn--primary"
              disabled={busy}
              onClick={() => void run(() => passkeyUnlock())}
            >
              {busy ? 'Waiting for the device' : 'Unlock with passkey'}
            </button>
            {deviceKnown ? (
              <>
                <div className="a-divider">or</div>
                <button type="button" className="a-btn" onClick={() => setShowPin(true)} disabled={busy}>
                  Unlock with PIN
                </button>
              </>
            ) : null}
          </>
        ) : null}

        <a className="a-btn a-btn--quiet" href="/login">
          Sign in as someone else
        </a>
      </div>
    </main>
  );
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lock',
  component: LockScreen,
  validateSearch: (search: Record<string, unknown>): LockSearch =>
    typeof search.next === 'string' ? { next: search.next } : {},
});
